import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ohif/ui-next';
import ToggleGroup from '../ToggleGroup';
import { TrackedAssessment } from '../../services/ReportAssessmentService';

/**
 * The callbacks carry the row's INDEX, not its findingId. This component already
 * distrusted `findingId` enough to key off the index when it is absent; the service
 * addresses rows the same way, so a report whose findings arrive without ids assesses
 * one row per click instead of all of them at once.
 *
 * The React key is that same index, which is safe for the same reason it is a usable
 * identity: the array is one server response, and nothing within a mount reorders it,
 * inserts into it or filters it — a verdict replaces one element in place.
 */
interface FindingsAssessmentProps {
  rows: readonly TrackedAssessment[];
  onClassificationChange: (index: number, value?: boolean) => void;
  onDetectionChange: (index: number, value?: boolean) => void;
}

export default function FindingsAssessment({
  rows,
  onClassificationChange,
  onDetectionChange,
}: FindingsAssessmentProps) {
  return (
    <Table
      noScroll
      className="text-sm"
    >
      <TableHeader>
        <TableRow className="hover:bg-transparent hover:[&>th]:text-white">
          <TableHead
            scope="col"
            className="px-1 font-semibold text-white"
          >
            {/* Visually empty, as today. The name exists so `scope` has something to
                associate a row with; sr-only keeps the header row unchanged by eye. */}
            <span className="sr-only">Finding</span>
          </TableHead>
          <TableHead
            scope="col"
            className="px-1 text-center font-semibold text-white"
          >
            Classification
          </TableHead>
          <TableHead
            scope="col"
            className="px-1 text-center font-semibold text-white"
          >
            Detection
          </TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        {rows.map((finding, index) => {
          /*
            §6 defect 11. develop rendered `Finding P{finding.name[finding.name.length - 1]}`,
            which assumes a P<n> naming convention AND throws a TypeError when `name` is
            absent — the stub's fixture omits it on f-003 deliberately, so the fix is
            observable. For a well-formed name the output is byte-identical: "P1" produced
            "Finding P" + "1".
          */
          const rowLabel = finding.name ? `Finding ${finding.name}` : `Finding ${index + 1}`;

          return (
            <TableRow
              key={index}
              // M5.3 D12. cn() (tailwind-merge) removes TableRow's two descendant hover
              // variants (hover:[&>th]:text-highlight, hover:[&>td]:text-highlight) --
              // confirmed absent from this row's own className. Tailwind binds their
              // replacements' :hover to the CELL, not the row (hover:[&>td]:text-white
              // compiles to `.hover\:[&>td]\:text-white > td:hover`), so <td> only shows
              // white while the pointer sits directly over it; elsewhere it is unstyled
              // and inherits brand.css's `tr.hover\:text-highlight:hover` grey. Neither
              // state is the orange swap this opt-out targets. <th> is white regardless,
              // from its own text-white class above.
              className="hover:[&>th]:text-white hover:[&>td]:text-white"
            >
              <TableHead
                scope="row"
                className="h-auto px-1 py-1 font-medium text-white"
              >
                {rowLabel}
              </TableHead>

              <TableCell className="px-1 py-1">
                <ToggleGroup
                  value={finding.isClassificationCorrect}
                  onChange={value => onClassificationChange(index, value)}
                  disabled={finding.isSubmitted}
                  label={`Classification correct for ${rowLabel}`}
                />
              </TableCell>

              <TableCell className="px-1 py-1">
                <ToggleGroup
                  value={finding.isDetectionCorrect}
                  onChange={value => onDetectionChange(index, value)}
                  disabled={finding.isSubmitted}
                  label={`Detection correct for ${rowLabel}`}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
